import {
	AssemblyOperationResponse,
	AssemblyOperationUpdateRequest,
	AssemblyTechMapResponse,
	AssemblyOperationsUpdateOrCreateRequest,
	useUpdateAssemblyTechMapMutation,
} from '@/shared/state/api/swagger';
import { COMPLECT_OPERATION_STEP_ID, useAppSelector } from '@/shared';
import { convertNumberToNumberStringWithDot } from '@/shared/core/utils/convert-string-to-number-string';

export const useAssemblyOperations = () => {
	const [updateAssemblyTechMap] = useUpdateAssemblyTechMapMutation();

	const userRole = useAppSelector((state) => state.auth.userProfile?.role);
	const isStorekeeperRole = userRole === 'storekeeper';

	const handleCurrentOperation = (
		data: AssemblyOperationUpdateRequest,
		techMap: AssemblyTechMapResponse,
		defaultOperations: AssemblyOperationResponse[],
	) => {
		return new Promise<AssemblyTechMapResponse>((resolve, reject) => {
			try {
				const { is_canceled, id } = techMap.current_assembly_operation as AssemblyOperationResponse;
				const { assembly_operations: operations } = techMap;
				if (!id || !operations) return;

				// создаем объект операции с измененными значениями
				const payload: AssemblyOperationUpdateRequest = {
					...data,
					is_canceled,
					id,
				};

				// вставляем объект операции на место измененного
				const operationsToUpdate = operations.map((op) =>
					op.id === id ? payload : (op as AssemblyOperationUpdateRequest),
				);

				// Для сборочных техкарт реализуем логику переноса значений через getAllOperationsWithCopyFields
				const { operationsWithCopyFields } = getAllOperationsWithCopyFields(
					payload,
					operationsToUpdate,
					defaultOperations,
				);

				// Кладовщик может обновлять только дефолтные операции
				const operationsToSend = isStorekeeperRole
					? operationsWithCopyFields.filter((op) => defaultOperations.some((defOp) => defOp.id === op.id))
					: operationsWithCopyFields;

				const operationsPayload: AssemblyOperationsUpdateOrCreateRequest = {
					assembly_operations_to_update: operationsToSend,
				};

				updateAssemblyTechMap({
					assemblyTechMapId: techMap.id,
					bodyUpdateAssemblyTechMap: {
						assembly_operations_payload: operationsPayload,
					},
				})
					.unwrap()
					.then((res) => {
						resolve(res);
					})
					.catch((err) => {
						reject(err);
					});
			} catch (error) {
				console.error('Update assembly operation error:', error);
				reject(error);
			}
		});
	};

	const handleNotCurrentOperation = (
		data: AssemblyOperationUpdateRequest,
		techMap: AssemblyTechMapResponse,
		defaultOperations: AssemblyOperationResponse[],
	) => {
		return new Promise<AssemblyOperationResponse[]>((resolve, reject) => {
			try {
				// Используем операции непосредственно из техкарты
				const operations = techMap.assembly_operations || [];

				// достаем значения предыдущего состояния измененного объекта операции
				const foundOperation = operations.find((op) => op.id === data.id);
				if (!foundOperation) return;

				const { id, is_canceled } = foundOperation;

				if (!id || !operations) return;

				// создаем объект операции с измененными значениями
				const payload: AssemblyOperationUpdateRequest = {
					...data,
					id,
					is_canceled,
				};

				// вставляем объект операции на место измененного
				const operationsToUpdate = operations.map((op) =>
					op.id === id ? payload : (op as AssemblyOperationUpdateRequest),
				);

				const { operationsWithCopyFields } = getAllOperationsWithCopyFields(
					payload,
					operationsToUpdate,
					defaultOperations,
				);

				const operationsPayload: AssemblyOperationsUpdateOrCreateRequest = {
					assembly_operations_to_update: operationsWithCopyFields,
				};

				// Отслеживаем пропущенные операции для возврата
				const allOperations = [...operationsWithCopyFields];

				const currentOperationIdx = allOperations.findIndex(
					(op) => op.id === techMap.current_assembly_operation?.id,
				);
				const selectedOperationIdx = allOperations.findIndex((op) => op.id === data.id);
				const skippedOperations = allOperations.slice(currentOperationIdx, selectedOperationIdx);

				updateAssemblyTechMap({
					assemblyTechMapId: techMap.id,
					bodyUpdateAssemblyTechMap: {
						assembly_operations_payload: operationsPayload,
					},
				})
					.unwrap()
					.then(() => {
						resolve(skippedOperations as AssemblyOperationResponse[]);
					})
					.catch((err) => {
						reject(err);
					});
			} catch (error) {
				console.error('Update assembly operation error: ', error);
				reject(error);
			}
		});
	};

	return { handleCurrentOperation, handleNotCurrentOperation };
};

// функция для переноса годных шт в поля кол-во шт - адаптированная для assembly operations
const getAllOperationsWithCopyFields = (
	payload: AssemblyOperationUpdateRequest,
	operationsPayload: AssemblyOperationUpdateRequest[],
	defaultOperations: AssemblyOperationResponse[],
) => {
	// Если это операция комплектования, не выполняем переносы
	if (payload.step_id === COMPLECT_OPERATION_STEP_ID) {
		return { operationsWithCopyFields: operationsPayload };
	}

	// Сортируем операции по step_id для корректного порядка
	const operations = [...operationsPayload].sort((a, b) => a.id - b.id);

	// Находим индекс заполняемой операции
	const filledCustomOperationIndex = operations.findIndex((op) => op.id === payload.id);

	if (filledCustomOperationIndex >= 0 && payload.count_out_number !== undefined) {
		const countOutNumber = Number(convertNumberToNumberStringWithDot(payload.count_out_number));

		if (operations.length - 4 === filledCustomOperationIndex || operations[operations.length - 4]?.is_canceled) {
			// Перенос годных из "Контрольная ПСИ" в раздел Кол-во "Сдача на СГД"
			// Предпоследняя кастомная операция -> первая дефолтная операция

			// Ищем первую дефолтную операцию (обычно это операция с наибольшим step_id)
			// В handleOperationCountChange используется sortedOperationsTypes.findIndex с проверкой step_id и is_default
			if (defaultOperations.length > 0) {
				const sdgOutIndex = operations.indexOf(defaultOperations[0]);

				if (sdgOutIndex >= 0) {
					operations[sdgOutIndex] = {
						...operations[sdgOutIndex],
						count_in_number: countOutNumber,
					};
				}
			}
		} else if (
			operations.length - 3 === filledCustomOperationIndex ||
			operations[operations.length - 3]?.is_canceled
		) {
			// Перенос годных из "Консервации" в раздел Кол-во "Приёмка на СГД"
			// Последняя кастомная операция -> вторая дефолтная операция

			// Ищем вторую дефолтную операцию
			if (defaultOperations.length > 1) {
				const sdgInIndex = operations.indexOf(defaultOperations[1]);

				if (sdgInIndex >= 0) {
					operations[sdgInIndex] = {
						...operations[sdgInIndex],
						count_in_number: countOutNumber,
					};
				}
			}
		} else {
			// Поиск следующей неотмененной операции
			let nextOperationIndex = -1;

			for (let i = filledCustomOperationIndex + 1; i < operations.length; i++) {
				if (!operations[i].is_canceled && operations[i].id >= payload.id) {
					nextOperationIndex = i;
					break;
				}
			}

			// Если найдена следующая неотмененная операция и она не имеет предустановленного значения
			if (nextOperationIndex > -1) {
				operations[nextOperationIndex] = {
					...operations[nextOperationIndex],
					count_in_number: countOutNumber,
				};
			}
		}

		// Расчет брака для текущей операции если есть значения для расчета
		if (payload.count_in_number !== undefined) {
			const countInNumber = Number(convertNumberToNumberStringWithDot(payload.count_in_number));
			const wastedValue = countInNumber - countOutNumber;

			operations[filledCustomOperationIndex] = {
				...operations[filledCustomOperationIndex],
				wasted: Number.isNaN(wastedValue) ? undefined : wastedValue,
			};
		}
	}

	return { operationsWithCopyFields: operations };
};
